import { useMemo, useState } from 'react'
import type { StateOfAiSummary } from '../../hooks/useStateOfAi'

const GENERIC_STATE_ARTWORK = '/social/omikina-state-of-ai-share.png'
const DATED_STATE_ARTWORK: Record<string, { src: string; alt: string }> = {
  '2026-08-30': {
    src: '/editorial/state-of-ai/2026-08-30.png',
    alt: 'A world map links power transmission, data centers, security signals, and autonomous robots in a dark cyan and magenta infrastructure scene.',
  },
}

interface StateOfAiArtworkCandidate {
  src: string
  alt: string
  label: string
  custom: boolean
}

function safeImageUrl(value: string | undefined): string | undefined {
  const url = value?.trim()
  return url && (url.startsWith('/') || url.startsWith('https://')) ? url : undefined
}

function stateOfAiArtworkCandidates(edition: StateOfAiSummary): StateOfAiArtworkCandidate[] {
  const candidates: StateOfAiArtworkCandidate[] = []
  const apiImage = safeImageUrl(edition.imageUrl)
  if (apiImage) candidates.push({
    src: apiImage,
    alt: edition.imageAlt?.trim() || `Custom editorial artwork for ${edition.headline}.`,
    label: edition.imageModel ? `CUSTOM EDITION ARTWORK · ${edition.imageModel}` : 'CUSTOM EDITION ARTWORK',
    custom: true,
  })
  const dated = DATED_STATE_ARTWORK[edition.date]
  if (dated) candidates.push({ ...dated, label: 'CUSTOM EDITION ARTWORK', custom: true })
  candidates.push({
    src: GENERIC_STATE_ARTWORK,
    alt: 'OMIKINA State of AI series artwork.',
    label: 'SERIES ARTWORK · CUSTOM EDITION IMAGE UNAVAILABLE',
    custom: false,
  })
  return candidates.filter((candidate, index, all) => all.findIndex((item) => item.src === candidate.src) === index)
}

export function StateOfAiArtwork({ edition, priority = false, className = '' }: { edition: StateOfAiSummary; priority?: boolean; className?: string }) {
  const candidates = useMemo(() => stateOfAiArtworkCandidates(edition), [edition])
  const candidateKey = candidates.map((candidate) => candidate.src).join('|')
  const [failedCandidate, setFailedCandidate] = useState({ key: candidateKey, index: 0 })
  const candidateIndex = failedCandidate.key === candidateKey ? failedCandidate.index : 0
  const candidate = candidates[Math.min(candidateIndex, candidates.length - 1)]

  return <figure className={`state-of-ai-artwork ${candidate.custom ? 'is-custom' : 'is-generic'} ${className}`.trim()}>
    <img
      src={candidate.src}
      alt={candidate.alt}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      decoding="async"
      onError={() => setFailedCandidate({ key: candidateKey, index: Math.min(candidateIndex + 1, candidates.length - 1) })}
    />
    <figcaption>{candidate.label}</figcaption>
  </figure>
}
